/**
 * FR-017.7 — how link targets map onto the farm explorer's lens registry.
 *
 * Kept free of any panel or i18n import: `PruefstatusPage` needs
 * `lensIdByEntityType` too, and importing it through the panel pulled the
 * whole translation setup into a page whose tests fake `react-i18next`.
 */
import type { LenseConfig } from '../../services/twinService';
import type { LinkTargetType } from '../../pages/documents/AddDocumentLinkForm';

/**
 * The concrete types a lens shows. A backend that predates `concrete_types`
 * yields the lens's own type — right for every lens but `agreements`, whose
 * links then stay plain text until the backend rolls, as they did before.
 */
function concreteTypes(lens: LenseConfig): Array<{ entity_type: string; name: string }> {
  if (lens.concrete_types) return lens.concrete_types;
  return lens.entity_type ? [{ entity_type: lens.entity_type, name: lens.name }] : [];
}

/**
 * entity_type → lens id, over each lens's CONCRETE types: a link targets a
 * stored row, and no row is stored under an abstract type. The first lens
 * showing a type wins.
 */
export function lensIdByEntityType(lenses: readonly LenseConfig[] | undefined): Map<string, string> {
  const byType = new Map<string, string>();
  for (const lens of lenses ?? []) {
    for (const t of concreteTypes(lens)) {
      if (!byType.has(t.entity_type)) byType.set(t.entity_type, lens.id);
    }
  }
  return byType;
}

/**
 * The object types "add" offers: one per concrete type, labelled by its lens
 * — plus its own name when one lens shows several ("Vertraege: Pachtvertrag").
 * Offering the abstract type instead searched by an `entity_type` nothing is
 * stored under and answered "Kein Treffer" for every contract (codex, #3 r1).
 */
export function linkTargetTypes(lenses: readonly LenseConfig[] | undefined): LinkTargetType[] {
  const seen = new Set<string>();
  const out: LinkTargetType[] = [];
  for (const lens of lenses ?? []) {
    const types = concreteTypes(lens);
    for (const t of types) {
      if (seen.has(t.entity_type)) continue;
      seen.add(t.entity_type);
      out.push({ entityType: t.entity_type, label: types.length > 1 ? `${lens.name}: ${t.name}` : lens.name });
    }
  }
  return out;
}
